import { useCallback, useEffect, useState } from "react";
import { FleetDefaultses } from "@app/shared/src/api/api.gen";
import { FleetDefaults } from "@app/shared/src/models";
import { useOrg } from "../components/providers/OrgProvider";

/**
 * The organization's stated alert defaults.
 *
 * The point of this record is that the console can show what the fleet is *meant* to be running,
 * rather than inferring it from the trucks and rendering "Mixed" the moment one has drifted. The
 * server creates the row from its own defaults on first read, so there is never an empty state to
 * design around.
 */
export function useFleetDefaultsRecord() {
    const { org } = useOrg();
    const [defaults, setDefaults] = useState<FleetDefaults>();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string>();

    const load = useCallback(async () => {
        if (!org?.id) return;
        setIsLoading(true);
        try {
            setDefaults(await FleetDefaultses.getForOrganization(org.id));
            setError(undefined);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not load the fleet defaults");
        } finally {
            setIsLoading(false);
        }
    }, [org?.id]);

    useEffect(() => {
        load();
    }, [load]);

    const save = useCallback(
        async (next: FleetDefaults) => {
            const saved = await FleetDefaultses.save(next);
            setDefaults(saved);
            return saved;
        },
        [],
    );

    return { defaults, isLoading, error, reload: load, save };
}
